// --- React
import { useContext, useReducer } from 'react';
// --- Context
import { DialogsCtx } from './context';

const DIALOG_FORM_ACTIONS = {
    SET_VALUE: 'DIALOGS/FORMS/SET_VALUE',
    SET_VALUES: 'DIALOGS/FORMS/SET_VALUES',
    SET_ERROR: 'DIALOGS/FORMS/SET_ERROR',
    RESET: 'DIALOGS/FORMS/RESET',
};

const initialState = {
    formData: {},
    errors: {},
    isDirty: false,
};

/**
 * Creates a nested object structure based on the provided array and value.
 * The last element of the array is set to the provided value.
 *
 * @function
 * @param {Array} arr - The array of keys representing the nested structure.
 * @param {*} value - The value to set at the deepest level of the object.
 * @return {Function} A function that takes a previous state and returns the updated state.
 */
function createNestedObject(arr, value) {
    let obj = {};
    let currentObj = obj;

    // Iterate through the array and create nested objects
    for (let i = 0; i < arr.length - 1; i++) {
        currentObj[arr[i]] = {};
        currentObj = currentObj[arr[i]];
    }

    // Set the value at the deepest level of the object
    currentObj[arr[arr.length - 1]] = value;

    // Return a function that takes the previous state and merges it with the new object
    return (prevState) => {
        return deepMerge(prevState, obj);
    };
}

/**
 * Merges two objects deeply.
 *
 * @function
 * @param {Object} target - The target object to merge into.
 * @param {Object} source - The source object to merge from.
 * @return {Object} The merged object.
 */
function deepMerge(target, source) {
    // Check if the target and source are both objects
    if (typeof target !== 'object' || typeof source !== 'object') {
        return source;
    }
    // If the target is null, return the source
    for (const key in source) {
        // Check if the key exists in the target
        if (source.hasOwnProperty(key)) {
            // If the key doesn't exist in the target, set it to the source value
            target[key] = deepMerge(target[key], source[key]);
        }
    }
    return target;
}

/**
 * Reducer function to manage the state of the dialogs form.
 * It handles setting values, errors, and resetting the form state.
 *
 * @function
 * @param {Object} state - The current state of the form.
 * @param {Object} action - The action dispatched to the reducer.
 * @return {Object} The updated state of the form.
 */
const dialogsFormReducer = (state = initialState, { type, payload }) => {
    if (type === DIALOG_FORM_ACTIONS.RESET) {
        return {
            formData: {},
            errors: {},
            isDirty: false,
        };
    }
    if (type === DIALOG_FORM_ACTIONS.SET_VALUE) {
        return {
            ...state,
            formData: createNestedObject(payload.name.split('.'), payload.value)(state.formData),
            isDirty: true,
        };
    }
    if (type === DIALOG_FORM_ACTIONS.SET_VALUES) {
        return {
            ...state,
            formData: Object.entries(payload).reduce((acc, [name, value]) => {
                return createNestedObject(name.split('.'), value)(acc);
            }, state.formData),
            isDirty: true,
        };
    }
    if (type === DIALOG_FORM_ACTIONS.SET_ERROR) {
        return {
            ...state,
            errors: payload,
        };
    }

    return state;
};

/**
 * useDialogsFormCtxBuilder Hook
 *
 * This hook provides functionality to manage the state of dialogs forms.
 * It uses the useReducer hook to manage the form state and provides functions to set values, set errors, and reset the form.
 *
 * @returns {Object} An object containing the form state and functions to manage it.
 */
export const useDialogsFormCtxBuilder = () => {
    const [state, dispatch] = useReducer(dialogsFormReducer, initialState);

    // Function to set a value in the form state
    const setValue = (name, value) => {
        dispatch({
            type: DIALOG_FORM_ACTIONS.SET_VALUE,
            payload: { name, value },
        });
    };

    const setValues = (values) => {
        dispatch({
            type: DIALOG_FORM_ACTIONS.SET_VALUES,
            payload: values,
        });
    };

    // Function to set an error in the form state
    const setError = (error) => {
        dispatch({
            type: DIALOG_FORM_ACTIONS.SET_ERROR,
            payload: error,
        });
    };

    // Function to reset the form state
    const reset = () => dispatch({ type: DIALOG_FORM_ACTIONS.RESET });

    return {
        state,
        setValue,
        setValues,
        setError,
        reset,
    };
};

export const useDialogsForm = () => useContext(DialogsCtx).form;
